import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { getAuth } from 'firebase/auth';
import Homepage from './pages/Homepage';
import SpanishHomepage from './pages/SpanishHomepage';
import PortugueseHomepage from './pages/PortugueseHomepage';
import JapaneseHomepage from './pages/JapaneseHomepage';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './pages/Dashboard';
import TestPage from './pages/TestPage';
import ResultPage from './components/ResultPage';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FullBlogPost from './pages/FullBlogPost';
import BlogIndex from './pages/BlogIndex';
import RefundPolicy from './pages/RefundPolicy';
import PracticeTestPage from './pages/PracticeTestPage';
import PracticeHub from './pages/PracticeHub';
import SpeakingHub from './pages/SpeakingHub';
import ReadingHub from './pages/ReadingHub';
import VeptHub from './pages/VeptHub';
import TcsHub from './pages/TcsHub';
import AmazonHub from './pages/AmazonHub';
import CognizantHub from './pages/CognizantHub';
import SpeakingTest from './pages/SpeakingTest';
import VeptTestPage from './pages/VeptTestPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import PricingPage from './pages/PricingPage';
import EbookLanding from './pages/EbookLanding';
import ThankYou from './pages/ThankYou';
import { Navigate } from 'react-router-dom';
import { useActivityTracker } from './hooks/useActivityTracker';
import { useSessionManager } from './hooks/useSessionManager';
import WhatsAppButton from './components/WhatsAppButton';
import JobAlertToast from './components/JobAlertToast';
import CookieConsentBanner from './components/CookieConsentBanner';
import { initUTMTracking, fetchGeoLocation } from './utils/GA4Analytics';

// Helper component to handle /admin redirection
const AdminRedirect = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user && user.email === 'admin@versantapp.com') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <Navigate to="/admin/login" replace />;
};



// Component to track page views, user activity, and enforce session security
function PageTracker() {
  const location = useLocation();

  useActivityTracker(); // <--- New Global Real-time Tracker
  useSessionManager();  // <--- Anti-Piracy Server Lock

  // Initialize UTM Tracking & Geolocation Fetch
  useEffect(() => {
    initUTMTracking();
    fetchGeoLocation();
  }, []);

  // Real-time Unique/Repeated Visitor Tracking
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        let visitorId = localStorage.getItem('versant_visitor_id');
        let isRepeated = true;

        if (!visitorId) {
          // Generate a new unique visitor ID
          visitorId = 'vis_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
          localStorage.setItem('versant_visitor_id', visitorId);
          isRepeated = false;
        }

        // Generate YYYY-MM-DD in local time
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`;

        const lastLoggedDate = localStorage.getItem('versant_last_visit_logged_date');
        if (lastLoggedDate !== todayStr) {
          const response = await fetch('/.netlify/functions/trackVisitor', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ visitorId, isRepeated, date: todayStr })
          });
          if (response.ok) {
            localStorage.setItem('versant_last_visit_logged_date', todayStr);
          }
        }
      } catch (err) {
        console.error("Error tracking visitor:", err);
      }
    };
    trackVisitor();
  }, []);

  useEffect(() => {
    // 0. Affiliate URL Tracking
    const searchParams = new URLSearchParams(location.search);
    const ref = searchParams.get('ref') || searchParams.get('affiliate');
    if (ref) {
      localStorage.setItem('versant_affiliate_ref', ref);
      console.log('Affiliate tracking code saved:', ref);
    }

    // 0.5 Google Translate cookie auto-translation integration
    const activeLocale = sessionStorage.getItem('versant_locale');
    const isLocalizedHomepage = 
      location.pathname === '/' ||
      location.pathname === '/es' || 
      location.pathname === '/pt' || 
      location.pathname === '/ja';

    const isTestRoute = 
      location.pathname.includes('/versant-reading-and-writing-mock-test/start') ||
      location.pathname.includes('/versant-speaking-and-listening-practice-test/start') ||
      location.pathname.includes('/versant-practice-exercises/') ||
      location.pathname === '/result';

    if (activeLocale && activeLocale !== 'en') {
      // Dynamically set HTML lang tag to let browsers know the current document locale
      document.documentElement.lang = activeLocale;

      if (!isTestRoute && !isLocalizedHomepage) {
        document.cookie = `googtrans=/en/${activeLocale}; path=/;`;
        document.cookie = `googtrans=/en/${activeLocale}; path=/; domain=.localhost;`;
        document.cookie = `googtrans=/en/${activeLocale}; path=/; domain=versantpro.com;`;
        document.cookie = `googtrans=/en/${activeLocale}; path=/; domain=.versantpro.com;`;

        if (!window.googleTranslateElementInit) {
          window.googleTranslateElementInit = () => {
            new window.google.translate.TranslateElement({
              pageLanguage: 'en',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: true
            }, 'google_translate_element');
          };
          const script = document.createElement('script');
          script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
          script.async = true;
          document.body.appendChild(script);
        }
      } else {
        document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.localhost;';
        document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=versantpro.com;';
        document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.versantpro.com;';
      }
    } else {
      document.documentElement.lang = 'en';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.localhost;';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=versantpro.com;';
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.versantpro.com;';
    }

    // Track Page View in Google Analytics 4
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search
      });
    }

    // Scroll to top of window and inner overflow containers on route navigation
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const scrollableElements = document.querySelectorAll('.overflow-auto, .overflow-y-auto');
    scrollableElements.forEach(el => {
      el.scrollTop = 0;
    });
  }, [location]);

  return null;
}

function App() {
  const paypalOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "AebCAZ3TaqDhTWtO-ST3ti-dfio5p3S1c1WDLem38Y71qIHcU4xwD18Zu1MK932m1D0CvK5uNzdptDDd",
    currency: "USD",
    intent: "capture"
  };

  return (
    <PayPalScriptProvider options={paypalOptions}>
      <Router>
        <PageTracker />
        <WhatsAppButton />
        <JobAlertToast />
        <CookieConsentBanner />
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/es" element={<SpanishHomepage />} />
          <Route path="/pt" element={<PortugueseHomepage />} />
          <Route path="/ja" element={<JapaneseHomepage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/versant-reading-and-writing-mock-test/start" element={<TestPage />} />
          <Route path="/result" element={<ResultPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<FullBlogPost />} />

          {/* Practice Hub Routes */}
          <Route path="/versant-practice-hub" element={<PracticeHub />} />
          <Route path="/versant-practice-exercises/:section" element={<PracticeTestPage />} />

          {/* Sub-Test Hubs & Exam Routes */}
          <Route path="/versant-reading-and-writing-mock-test" element={<ReadingHub />} />
          <Route path="/versant-speaking-and-listening-practice-test" element={<SpeakingHub />} />
          <Route path="/versant-vept-mock-test" element={<VeptHub />} />
          <Route path="/versant-test-for-tcs" element={<TcsHub />} />
          <Route path="/versant-test-for-amazon" element={<AmazonHub />} />
          <Route path="/versant-test-for-cognizant" element={<CognizantHub />} />
          <Route path="/versant-speaking-and-listening-practice-test/start/:mode" element={<SpeakingTest />} />
          <Route path="/versant-vept-mock-test/start/:testId" element={<VeptTestPage />} />

          {/* Ebook Routes */}
          <Route path="/ebook" element={<EbookLanding />} />
          <Route path="/thank-you" element={<ThankYou />} />

          {/* Admin Routing */}
          <Route path="/admin" element={<AdminRedirect />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </Router>
    </PayPalScriptProvider>
  );
}

export default App;
